'use strict';

window.LogicCraft = window.LogicCraft || {};

window.LogicCraft.state = {
    circuit: new Map(),
    history: [],
    redoStack: [],
    currentSelection: { type: 'WIRE', switchId: 'A', inputCount: 2 },
    currentRotation: 0,
    focusedNodeKey: null,
    lockedInspectionNodeKey: null,
    modalLockedNodeKey: null,
    currentOpenModalType: null,
    currentBcdSegmentSelected: 'a',
    karnaughGroups: [],
    selectedKarnaughMinterms: [],
    nextGroupId: 1,
    airWires: []
};

window.LogicCraft.core = (function() {
    const state = window.LogicCraft.state;

    const GRID_SIZE = 0.5;
    function snap(val) { return Math.round(val / GRID_SIZE) * GRID_SIZE; }
    const key = (x, y, z) => `${snap(x)},${snap(y)},${snap(z)}`;

    const ACTUATOR_LINK_DISTANCE = 3.0;
    const HYDRAULIC_LINK_DISTANCE = 3.0;

    const COMP = {
        WIRE: 'WIRE',
        JUMPER: 'JUMPER',
        SWITCH: 'SWITCH',
        PUSH_BUTTON_NO: 'PUSH_BUTTON_NO',
        PUSH_BUTTON_NC: 'PUSH_BUTTON_NC',
        EMERGENCY_STOP: 'EMERGENCY_STOP',
        NOT: 'NOT',
        BUFFER: 'BUFFER',
        AND: 'AND',
        OR: 'OR',
        NAND: 'NAND',
        NOR: 'NOR',
        XOR: 'XOR',
        XNOR: 'XNOR',
        DEC_BCD_7SEG: 'DEC_BCD_7SEG',
        DISPLAY_7SEG: 'DISPLAY_7SEG',
        LAMP: 'LAMP',
        LED_RED: 'LED_RED',
        BUZZER: 'BUZZER',
        RELAY: 'RELAY',
        TIMER_TON: 'TIMER_TON',
        SOLENOID_PNEU: 'SOLENOID_PNEU',
        VALVE_3_2_MONO: 'VALVE_3_2_MONO',
        VALVE_5_2_MONO: 'VALVE_5_2_MONO',
        VALVE_5_2_BI: 'VALVE_5_2_BI',
        VALVE_FLOW_CONTROL: 'VALVE_FLOW_CONTROL',
        CYL_SINGLE: 'CYL_SINGLE',
        CYL_DOUBLE: 'CYL_DOUBLE',
        SOLENOID_HYDRA: 'SOLENOID_HYDRA',
        VALVE_4_2_MONO: 'VALVE_4_2_MONO',
        VALVE_4_2_BI: 'VALVE_4_2_BI',
        CYL_HYDRA: 'CYL_HYDRA',
        SUPPLY_PNEU: 'SUPPLY_PNEU',
        SUPPLY_HYDRA: 'SUPPLY_HYDRA',
        MOTOR_INDUCTION_6P: 'MOTOR_INDUCTION_6P',
        MOTOR_INDUCTION_MONO: 'MOTOR_INDUCTION_MONO',
        CONTACTOR_3P: 'CONTACTOR_3P',
        POWER_SUPPLY_3P: 'POWER_SUPPLY_3P'
    };

    const MACHINE_TYPES = new Set([
        COMP.MOTOR_INDUCTION_6P, COMP.MOTOR_INDUCTION_MONO
    ]);

    const CONTACTOR_TYPES = new Set([
        COMP.CONTACTOR_3P
    ]);

    const GATE_TYPES = new Set([
        COMP.NOT, COMP.BUFFER, COMP.AND, COMP.OR,
        COMP.NAND, COMP.NOR, COMP.XOR, COMP.XNOR
    ]);

    const BUTTON_TYPES = new Set([
        COMP.PUSH_BUTTON_NO, COMP.PUSH_BUTTON_NC, COMP.EMERGENCY_STOP
    ]);

    const COMBINATIONAL_TYPES = new Set([COMP.DEC_BCD_7SEG]);

    const ACTUATOR_TYPES = new Set([
        COMP.LAMP, COMP.LED_RED, COMP.BUZZER, COMP.SOLENOID_PNEU, COMP.SOLENOID_HYDRA, COMP.DISPLAY_7SEG, COMP.RELAY, COMP.TIMER_TON, COMP.CONTACTOR_3P
    ]);

    const VALVE_TYPES = new Set([
        COMP.VALVE_3_2_MONO, COMP.VALVE_5_2_MONO, COMP.VALVE_5_2_BI, COMP.VALVE_4_2_MONO, COMP.VALVE_4_2_BI
    ]);

    const CYLINDER_TYPES = new Set([
        COMP.CYL_SINGLE, COMP.CYL_DOUBLE, COMP.CYL_HYDRA
    ]);

    const SUPPLY_TYPES = new Set([
        COMP.SUPPLY_PNEU, COMP.SUPPLY_HYDRA, COMP.POWER_SUPPLY_3P
    ]);

    const FLOW_CONTROL_TYPES = new Set([
        COMP.VALVE_FLOW_CONTROL
    ]);

    const INPUT_CONFIG = [
        { id: 'A', label: 'Chave A', color: 0x00e5ff, hex: '#00e5ff' },
        { id: 'B', label: 'Chave B', color: 0xd946ef, hex: '#d946ef' },
        { id: 'C', label: 'Chave C', color: 0xf59e0b, hex: '#f59e0b' },
        { id: 'D', label: 'Chave D', color: 0x22c55e, hex: '#22c55e' }
    ];

    const BCD_TABLE = [
        { dec: 0, a:0, b:0, c:0, d:0, segs: { a:1, b:1, c:1, d:1, e:1, f:1, g:0 } },
        { dec: 1, a:0, b:0, c:0, d:1, segs: { a:0, b:1, c:1, d:0, e:0, f:0, g:0 } },
        { dec: 2, a:0, b:0, c:1, d:0, segs: { a:1, b:1, c:0, d:1, e:1, f:0, g:1 } },
        { dec: 3, a:0, b:0, c:1, d:1, segs: { a:1, b:1, c:1, d:1, e:0, f:0, g:1 } },
        { dec: 4, a:0, b:1, c:0, d:0, segs: { a:0, b:1, c:1, d:0, e:0, f:1, g:1 } },
        { dec: 5, a:0, b:1, c:0, d:1, segs: { a:1, b:0, c:1, d:1, e:0, f:1, g:1 } },
        { dec: 6, a:0, b:1, c:1, d:0, segs: { a:1, b:0, c:1, d:1, e:1, f:1, g:1 } },
        { dec: 7, a:0, b:1, c:1, d:1, segs: { a:1, b:1, c:1, d:0, e:0, f:0, g:0 } },
        { dec: 8, a:1, b:0, c:0, d:0, segs: { a:1, b:1, c:1, d:1, e:1, f:1, g:1 } },
        { dec: 9, a:1, b:0, c:0, d:1, segs: { a:1, b:1, c:1, d:1, e:0, f:1, g:1 } }
    ];

    function pushHistory(action) {
        state.history.push(action);
        if (state.history.length > 50) state.history.shift();
        state.redoStack = [];
    }

    function saveCircuitToLocalStorage() {
        try {
            const components = serializeCircuit();
            const airWires = (state.airWires || []).map(w => ({
                from: { nodeX: w.from.nodeX, nodeY: w.from.nodeY, nodeZ: w.from.nodeZ, pinId: w.from.pinId },
                to: { nodeX: w.to.nodeX, nodeY: w.to.nodeY, nodeZ: w.to.nodeZ, pinId: w.to.pinId },
                isFromSwitch: Boolean(w.isFromSwitch)
            }));
            const hoses = (state.hoses || []).map(h => ({
                from: { nodeX: h.from.nodeX, nodeY: h.from.nodeY, nodeZ: h.from.nodeZ, portId: h.from.portId },
                to: { nodeX: h.to.nodeX, nodeY: h.to.nodeZ !== undefined ? h.to.nodeY : 0, nodeZ: h.to.nodeZ, portId: h.to.portId },
                hoseType: h.hoseType || 'pneu'
            }));
            const payload = {
                components,
                airWires,
                hoses
            };
            if (window.LogicCraftGuard?.secureSave) {
                window.LogicCraftGuard.secureSave('logiccraft_autosave_circuit', payload);
            } else {
                localStorage.setItem('logiccraft_autosave_circuit', JSON.stringify(payload));
            }
        } catch (e) {
            console.warn('[LogicCraft Core] Falha no auto-save:', e);
        }
    }

    function loadCircuitFromLocalStorage() {
        try {
            let data = null;
            if (window.LogicCraftGuard?.secureLoad) {
                data = window.LogicCraftGuard.secureLoad('logiccraft_autosave_circuit');
            }
            if (!data) {
                const raw = localStorage.getItem('logiccraft_autosave_circuit');
                if (raw) {
                    const parsed = JSON.parse(raw);
                    data = (parsed && typeof parsed === 'object' && parsed.data !== undefined) ? parsed.data : parsed;
                }
            }
            return data;
        } catch (e) {
            return null;
        }
    }

    function computeBcd7Segment(inputs) {
        const a = inputs.A ? 1 : 0;
        const b = inputs.B ? 1 : 0;
        const c = inputs.C ? 1 : 0;
        const d = inputs.D ? 1 : 0;
        const value = (a << 3) | (b << 2) | (c << 1) | d;

        if (value >= 0 && value <= 9) {
            const item = BCD_TABLE[value];
            return {
                valid: true,
                value: value,
                digit: value,
                segments: {
                    a: Boolean(item.segs.a),
                    b: Boolean(item.segs.b),
                    c: Boolean(item.segs.c),
                    d: Boolean(item.segs.d),
                    e: Boolean(item.segs.e),
                    f: Boolean(item.segs.f),
                    g: Boolean(item.segs.g)
                },
                message: null
            };
        }
        return {
            valid: false,
            value: value,
            digit: null,
            segments: { a: false, b: false, c: false, d: false, e: false, f: false, g: false },
            message: 'Código BCD inválido (Entrada fora de 0..9).'
        };
    }

    function getBcdSegmentMinterms(segmentId) {
        const list = [];
        for (let i = 0; i <= 9; i++) {
            if (BCD_TABLE[i].segs[segmentId] === 1) list.push(i);
        }
        return list;
    }

    function getBcdDontCareMinterms() {
        return [10, 11, 12, 13, 14, 15];
    }

    function getDigitalWordState() {
        const swMap = { A: 0, B: 0, C: 0, D: 0 };
        state.circuit.forEach(node => {
            if (node.type === COMP.SWITCH && swMap[node.switchId] !== undefined) {
                swMap[node.switchId] = node.manualValue ? 1 : 0;
            }
        });
        const bits = [swMap.A, swMap.B, swMap.C, swMap.D];
        const binary = bits.join('');
        const decimal = (swMap.A << 3) | (swMap.B << 2) | (swMap.C << 1) | swMap.D;
        const hexadecimal = decimal.toString(16).toUpperCase();
        return { bits, binary, decimal, hexadecimal };
    }

    function computeGateLogic(type, inputs) {
        const countHigh = inputs.filter(Boolean).length;
        switch (type) {
            case COMP.NOT:    return !inputs[0];
            case COMP.BUFFER: return Boolean(inputs[0]);
            case COMP.AND:    return inputs.length > 0 && inputs.every(Boolean);
            case COMP.OR:     return inputs.some(Boolean);
            case COMP.NAND:   return !(inputs.length > 0 && inputs.every(Boolean));
            case COMP.NOR:    return !inputs.some(Boolean);
            case COMP.XOR:    return (countHigh % 2) !== 0;
            case COMP.XNOR:   return (countHigh % 2) === 0;
            default: return false;
        }
    }

    function generateTruthTable(gateType, inputCount) {
        const rows = [];
        const totalRows = Math.pow(2, inputCount);
        for (let i = 0; i < totalRows; i++) {
            const pattern = [];
            for (let b = inputCount - 1; b >= 0; b--) pattern.push(((i >> b) & 1) === 1);
            const s = computeGateLogic(gateType, pattern);
            rows.push({ minterm: i, inputs: pattern.map(b => (b ? 1 : 0)), output: s ? 1 : 0 });
        }
        return rows;
    }

    function getActiveTruthTableRow(inputs) {
        return inputs.reduce((acc, bit) => (acc << 1) | Number(bit), 0);
    }

    function getGateExpression(type, n) {
        const vars = ['A', 'B', 'C', 'D'].slice(0, n);
        switch (type) {
            case COMP.NOT:             return 'S = ¬A';
            case COMP.BUFFER:          return 'S = A';
            case COMP.AND:             return `S = ${vars.join(' · ')}`;
            case COMP.OR:              return `S = ${vars.join(' + ')}`;
            case COMP.NAND:            return `S = ¬(${vars.join(' · ')})`;
            case COMP.NOR:             return `S = ¬(${vars.join(' + ')})`;
            case COMP.XOR:             return `S = ${vars.join(' ⊕ ')}`;
            case COMP.XNOR:            return `S = ¬(${vars.join(' ⊕ ')})`;
            case COMP.DEC_BCD_7SEG:    return 'a..g = decodificador(A, B, C, D) [BCD 0..9]';
            case COMP.RELAY:           return 'NO = A1 · COM | NC = ¬A1 · COM';
            case COMP.CONTACTOR_3P:    return 'K1 = A1/A2 · (1/L1→2/T1 · 3/L2→4/T2 · 5/L3→6/T3 · 13→14 NA)';
            case COMP.POWER_SUPPLY_3P: return 'Rede 380V/220V = L1, L2, L3 + N + PE (Aterramento NR-10)';
            case COMP.TIMER_TON:       return 'Q = TON(IN, PT=2.0s) [Atraso na Ligação]';
            case COMP.PUSH_BUTTON_NO:  return 'OUT = Botoeira NA (Nível 1 com clique alternado / toggle)';
            case COMP.PUSH_BUTTON_NC:  return 'OUT = Botoeira NF (Nível 0 com clique alternado / toggle)';
            case COMP.EMERGENCY_STOP:  return 'EMG = Cogumelo NR-12 com Trava (Abre comando ao atuar)';
            case COMP.SOLENOID_PNEU:   return 'Y1 = Bobina Solenoide 24VDC (Bornes A1/A2)';
            case COMP.SOLENOID_HYDRA:  return 'YH1 = Bobina Hidráulica 24VDC (Bornes A1/A2)';
            case COMP.VALVE_3_2_MONO:  return 'Válvula 3/2 Vias Monoestável (Retorno por mola, Solenoide Y1 - ISO 1219)';
            case COMP.VALVE_5_2_MONO:  return 'Válvula 5/2 Vias Monoestável (Retorno por mola, Solenoide Y1 - ISO 1219)';
            case COMP.VALVE_5_2_BI:    return 'Válvula 5/2 Vias Biestável (Duplo Solenoide Y1 e Y2 - ISO 1219)';
            case COMP.VALVE_4_2_MONO:  return 'Válvula 4/2 Vias Monoestável (Retorno por mola, Solenoide YH1, Pórticos P/T - 120 bar)';
            case COMP.VALVE_4_2_BI:    return 'Válvula 4/2 Vias Biestável (Duplo Solenoide YH1 e YH2, Pórticos P/T - 120 bar)';
            case COMP.VALVE_FLOW_CONTROL: return 'Válvula Reguladora de Fluxo Unidirecional (0% a 100%)';
            case COMP.CYL_SINGLE:      return 'Cilindro Simples Ação (Retorno por Mola Helicoidal Mecânica)';
            case COMP.CYL_DOUBLE:      return 'Cilindro Dupla Ação Pneumático (Alumínio Anodizado c/ Sensores S0/S1)';
            case COMP.CYL_HYDRA:       return 'Cilindro Dupla Ação Hidráulico (Aço Forjado 120 bar, ISO VG 46)';
            case COMP.SUPPLY_PNEU:     return 'Unidade de Conservação FRL / Compressor 6 bar (Alimentação Pneumática)';
            case COMP.SUPPLY_HYDRA:    return 'Unidade Hidráulica / Motor-Bomba 120 bar (Alimentação Pressão P / Tanque T)';
            default: return '';
        }
    }

    function getDeMorganEquivalent(type, n) {
        const vars = ['A', 'B', 'C', 'D'].slice(0, n);
        if (type === COMP.NAND) {
            return {
                original: `S = ¬(${vars.join(' · ')})`,
                equivalent: `S = ${vars.map(v => `¬${v}`).join(' + ')}`,
                law: 'Teorema de De Morgan: o complemento do produto é igual à soma dos complementos.'
            };
        }
        if (type === COMP.NOR) {
            return {
                original: `S = ¬(${vars.join(' + ')})`,
                equivalent: `S = ${vars.map(v => `¬${v}`).join(' · ')}`,
                law: 'Teorema de De Morgan: o complemento da soma é igual ao produto dos complementos.'
            };
        }
        return null;
    }

    function rotateGridOffset(offset, rot) {
        switch (rot) {
            case 0: return { x: offset.x, z: offset.z };
            case 1: return { x: offset.z, z: -offset.x };
            case 2: return { x: -offset.x, z: -offset.z };
            case 3: return { x: -offset.z, z: offset.x };
            default: return { x: offset.x, z: offset.z };
        }
    }

    function getLocalGatePins(inputCount) {
        const pins = { inputs: [], outputs: [{ id: 'S', x: 0.45, y: -0.27, z: 0.0, aliases: ['S', 'OUT'] }] };
        switch (inputCount) {
            case 1:
                pins.inputs = [
                    { id: 'A', x: -0.45, y: -0.27, z: 0.0, aliases: ['A', 'IN'] }
                ];
                break;
            case 2:
                pins.inputs = [
                    { id: 'A', x: -0.45, y: -0.27, z: -0.24, aliases: ['A'] },
                    { id: 'B', x: -0.45, y: -0.27, z:  0.24, aliases: ['B'] }
                ];
                break;
            case 3:
                pins.inputs = [
                    { id: 'A', x: -0.45, y: -0.27, z: -0.32, aliases: ['A'] },
                    { id: 'B', x: -0.45, y: -0.27, z:  0.00, aliases: ['B'] },
                    { id: 'C', x: -0.45, y: -0.27, z:  0.32, aliases: ['C'] }
                ];
                break;
            case 4:
            default:
                pins.inputs = [
                    { id: 'A', x: -0.45, y: -0.27, z: -0.36, aliases: ['A'] },
                    { id: 'B', x: -0.45, y: -0.27, z: -0.12, aliases: ['B'] },
                    { id: 'C', x: -0.45, y: -0.27, z:  0.12, aliases: ['C'] },
                    { id: 'D', x: -0.45, y: -0.27, z:  0.36, aliases: ['D'] }
                ];
                break;
        }
        return pins;
    }

    function getLocalComponentPins(type, inputCount, posS0, posS1) {
        if (type === COMP.DEC_BCD_7SEG) {
            return {
                inputs: [
                    { id: 'A', x: -1.5, y: -0.38, z: -0.5, aliases: ['A'] },
                    { id: 'B', x: -0.5, y: -0.38, z: -0.5, aliases: ['B'] },
                    { id: 'C', x:  0.5, y: -0.38, z: -0.5, aliases: ['C'] },
                    { id: 'D', x:  1.5, y: -0.38, z: -0.5, aliases: ['D'] }
                ],
                outputs: [
                    { id: 'a', x: -1.5, y: -0.38, z: 0.5, aliases: ['a'] },
                    { id: 'b', x: -1.0, y: -0.38, z: 0.5, aliases: ['b'] },
                    { id: 'c', x: -0.5, y: -0.38, z: 0.5, aliases: ['c'] },
                    { id: 'd', x:  0.0, y: -0.38, z: 0.5, aliases: ['d'] },
                    { id: 'e', x:  0.5, y: -0.38, z: 0.5, aliases: ['e'] },
                    { id: 'f', x:  1.0, y: -0.38, z: 0.5, aliases: ['f'] },
                    { id: 'g', x:  1.5, y: -0.38, z: 0.5, aliases: ['g'] }
                ]
            };
        }
        if (type === COMP.DISPLAY_7SEG) {
            return {
                inputs: [
                    { id: 'a', x: -1.5, y: -0.12, z: -0.5, aliases: ['a'] },
                    { id: 'b', x: -1.0, y: -0.12, z: -0.5, aliases: ['b'] },
                    { id: 'c', x: -0.5, y: -0.12, z: -0.5, aliases: ['c'] },
                    { id: 'd', x:  0.0, y: -0.12, z: -0.5, aliases: ['d'] },
                    { id: 'e', x:  0.5, y: -0.12, z: -0.5, aliases: ['e'] },
                    { id: 'f', x:  1.0, y: -0.12, z: -0.5, aliases: ['f'] },
                    { id: 'g', x:  1.5, y: -0.12, z: -0.5, aliases: ['g'] }
                ],
                outputs: []
            };
        }
        if (type === COMP.RELAY) {
            return {
                inputs: [
                    { id: 'A1',  x: -0.55, y: -0.32, z: -0.55, aliases: ['A1'] },
                    { id: 'COM', x:  0.55, y: -0.32, z: -0.55, aliases: ['COM'] }
                ],
                outputs: [
                    { id: 'NO', x: -0.55, y: -0.32, z: 0.55, aliases: ['NO', '14'] },
                    { id: 'NC', x:  0.55, y: -0.32, z: 0.55, aliases: ['NC', '12'] }
                ]
            };
        }
        if (type === COMP.CONTACTOR_3P) {
            return {
                inputs: [
                    { id: 'A1',   x: -0.44, y: -0.22, z: -0.50, aliases: ['A1'] },
                    { id: '1/L1', x: -0.22, y: -0.22, z: -0.50, aliases: ['1', 'L1', '1/L1'] },
                    { id: '3/L2', x:  0.00, y: -0.22, z: -0.50, aliases: ['3', 'L2', '3/L2'] },
                    { id: '5/L3', x:  0.22, y: -0.22, z: -0.50, aliases: ['5', 'L3', '5/L3'] },
                    { id: '13',   x:  0.44, y: -0.22, z: -0.50, aliases: ['13', 'NO1', '13/NO'] }
                ],
                outputs: [
                    { id: 'A2',   x: -0.44, y: -0.22, z:  0.50, aliases: ['A2'] },
                    { id: '2/T1', x: -0.22, y: -0.22, z:  0.50, aliases: ['2', 'T1', '2/T1'] },
                    { id: '4/T2', x:  0.00, y: -0.22, z:  0.50, aliases: ['4', 'T2', '4/T2'] },
                    { id: '6/T3', x:  0.22, y: -0.22, z:  0.50, aliases: ['6', 'T3', '6/T3'] },
                    { id: '14',   x:  0.44, y: -0.22, z:  0.50, aliases: ['14', 'NO2', '14/NO'] }
                ]
            };
        }
        if (type === COMP.LAMP || type === COMP.LED_RED || type === COMP.BUZZER) {
            return {
                inputs: [
                    { id: 'X1', x: -0.22, y: -0.15, z: 0.28, label: 'X1', aliases: ['A1', '1', 'X1'] },
                    { id: 'X2', x:  0.22, y: -0.15, z: 0.28, label: 'X2', aliases: ['A2', '2', 'GND', 'X2'] }
                ],
                outputs: []
            };
        }
        if (type === COMP.POWER_SUPPLY_3P) {
            return {
                inputs: [],
                outputs: [
                    { id: 'L1', x: -0.44, y: -0.15, z: 0.35, aliases: ['L1'] },
                    { id: 'L2', x: -0.22, y: -0.15, z: 0.35, aliases: ['L2'] },
                    { id: 'L3', x:  0.00, y: -0.15, z: 0.35, aliases: ['L3'] },
                    { id: 'N',  x:  0.22, y: -0.15, z: 0.35, aliases: ['N'] },
                    { id: 'PE', x:  0.44, y: -0.15, z: 0.35, aliases: ['PE', 'GND', 'TERRA'] }
                ]
            };
        }
        if (type === COMP.TIMER_TON) {
            return {
                inputs: [
                    { id: 'IN', x: -0.5, y: -0.20, z: -0.5, aliases: ['IN', 'A1'] }
                ],
                outputs: [
                    { id: 'Q', x: 0.5, y: -0.20, z: 0.5, aliases: ['Q', 'OUT'] }
                ]
            };
        }
        if (VALVE_TYPES.has(type)) {
            const isBi = (type === COMP.VALVE_5_2_BI || type === COMP.VALVE_4_2_BI);
            const isHydra = (type === COMP.VALVE_4_2_MONO || type === COMP.VALVE_4_2_BI);
            const inList = [
                { id: 'Y1', x: -0.62, y: -0.20, z: 0.0, aliases: ['Y1', 'A1', 'YH1'] }
            ];
            if (isHydra) inList.push({ id: 'YH1', x: -0.62, y: -0.20, z: 0.0, aliases: ['YH1', 'Y1', 'A1'] });
            if (isBi) {
                inList.push({ id: 'Y2', x: 0.62, y: -0.20, z: 0.0, aliases: ['Y2', 'A2', 'YH2'] });
                if (isHydra) inList.push({ id: 'YH2', x: 0.62, y: -0.20, z: 0.0, aliases: ['YH2', 'Y2', 'A2'] });
            }
            return { inputs: inList, outputs: [] };
        }
        if (type === COMP.CYL_DOUBLE || type === COMP.CYL_HYDRA || type === COMP.CYL_SINGLE) {
            const p0 = typeof posS0 === 'number' ? posS0 : 0.05;
            const p1 = typeof posS1 === 'number' ? posS1 : 0.95;
            const z0 = -0.55 + p0 * 1.10;
            const z1 = -0.55 + p1 * 1.10;
            return {
                inputs: [],
                outputs: [
                    { id: 'S0', x: -0.36, y: -0.27, z: z0, aliases: ['S0'] },
                    { id: 'S1', x: -0.36, y: -0.27, z: z1, aliases: ['S1'] }
                ]
            };
        }
        if (type === COMP.SUPPLY_PNEU) {
            return {
                inputs: [],
                outputs: [
                    { id: 'P', x: 0.0, y: -0.32, z: 0.5, aliases: ['P', '1(P)'] }
                ]
            };
        }
        if (type === COMP.SUPPLY_HYDRA) {
            return {
                inputs: [],
                outputs: [
                    { id: 'P', x: -0.3, y: -0.30, z: 0.5, aliases: ['P'] },
                    { id: 'T', x:  0.3, y: -0.30, z: 0.5, aliases: ['T'] }
                ]
            };
        }
        if (type === COMP.SOLENOID_PNEU || type === COMP.SOLENOID_HYDRA) {
            return {
                inputs: [
                    { id: 'A1', x: 0.0, y: -0.38, z: -GRID_SIZE, aliases: ['A1', 'Y1', 'YH1'] }
                ],
                outputs: []
            };
        }
        if (type === COMP.SWITCH) {
            return {
                inputs: [],
                outputs: [
                    { id: 'OUT', x: 0.0, y: -0.38, z: 0.5, aliases: ['OUT', 'A', 'B', 'C', 'D'] }
                ]
            };
        }
        if (BUTTON_TYPES.has(type)) {
            return {
                inputs: [],
                outputs: [
                    { id: 'OUT', x: 0.0, y: -0.38, z: 0.5, aliases: ['OUT'] }
                ]
            };
        }
        if (type === COMP.MOTOR_INDUCTION_6P) {
            return {
                inputs: [
                    { id: 'W2', x: -0.20, y: 0.65, z: -0.15, aliases: ['W2'] },
                    { id: 'U2', x:  0.00, y: 0.65, z: -0.15, aliases: ['U2'] },
                    { id: 'V2', x:  0.20, y: 0.65, z: -0.15, aliases: ['V2'] },
                    { id: 'U1', x: -0.20, y: 0.65, z:  0.15, aliases: ['U1'] },
                    { id: 'V1', x:  0.00, y: 0.65, z:  0.15, aliases: ['V1'] },
                    { id: 'W1', x:  0.20, y: 0.65, z:  0.15, aliases: ['W1'] },
                    { id: 'PE', x: -0.34, y: 0.65, z:  0.00, aliases: ['PE', 'GND', 'TERRA'] }
                ],
                outputs: []
            };
        }
        if (type === COMP.MOTOR_INDUCTION_MONO) {
            return {
                inputs: [
                    { id: 'U2', x: -0.16, y: 0.60, z: -0.14, aliases: ['U2'] },
                    { id: 'Z2', x:  0.16, y: 0.60, z: -0.14, aliases: ['Z2'] },
                    { id: 'U1', x: -0.16, y: 0.60, z:  0.14, aliases: ['U1'] },
                    { id: 'Z1', x:  0.16, y: 0.60, z:  0.14, aliases: ['Z1'] },
                    { id: 'PE', x: -0.28, y: 0.60, z:  0.00, aliases: ['PE', 'GND', 'TERRA'] }
                ],
                outputs: []
            };
        }
        return getLocalGatePins(inputCount ?? 2);
    }

    function getLocalComponentFluidPorts(type) {
        if (type === COMP.SUPPLY_PNEU) {
            return [
                { id: '1(P)', x: 0.0, y: -0.32, z: 0.48, isSupply: true, isHydra: false },
                { id: 'P', x: 0.0, y: -0.32, z: 0.48, isSupply: true, isHydra: false }
            ];
        }
        if (type === COMP.SUPPLY_HYDRA) {
            return [
                { id: 'P', x: -0.30, y: -0.30, z: 0.60, isSupply: true, isHydra: true },
                { id: 'T', x:  0.30, y: -0.30, z: 0.60, isTank: true, isHydra: true }
            ];
        }
        if (type === COMP.VALVE_3_2_MONO) {
            return [
                { id: '1(P)', x: -0.20, y: -0.32, z: -0.48, isInlet: true, isHydra: false },
                { id: '3(R)', x:  0.20, y: -0.32, z: -0.48, isTank: true, isHydra: false },
                { id: '2(A)', x:  0.00, y: -0.32, z:  0.48, isWork: true, isHydra: false }
            ];
        }
        if (type === COMP.VALVE_5_2_MONO || type === COMP.VALVE_5_2_BI) {
            return [
                { id: '5(S)', x: -0.25, y: -0.32, z: -0.48, isTank: true, isHydra: false },
                { id: '1(P)', x:  0.00, y: -0.32, z: -0.48, isInlet: true, isHydra: false },
                { id: '3(R)', x:  0.25, y: -0.32, z: -0.48, isTank: true, isHydra: false },
                { id: '4(A)', x: -0.25, y: -0.32, z:  0.48, isWork: true, isRear: true, isHydra: false },
                { id: '2(B)', x:  0.25, y: -0.32, z:  0.48, isWork: true, isFront: true, isHydra: false }
            ];
        }
        if (type === COMP.VALVE_4_2_MONO || type === COMP.VALVE_4_2_BI) {
            return [
                { id: 'P', x: -0.25, y: -0.32, z: -0.48, isInlet: true, isHydra: true },
                { id: 'T', x:  0.25, y: -0.32, z: -0.48, isTank: true, isHydra: true },
                { id: 'A', x: -0.25, y: -0.32, z:  0.48, isWork: true, isRear: true, isHydra: true },
                { id: 'B', x:  0.25, y: -0.32, z:  0.48, isWork: true, isFront: true, isHydra: true }
            ];
        }
        if (type === COMP.VALVE_FLOW_CONTROL) {
            return [
                { id: '1', x: 0.0, y: -0.32, z: -0.48, isInlet: true, isHydra: false },
                { id: '2', x: 0.0, y: -0.32, z:  0.48, isWork: true, isHydra: false }
            ];
        }
        if (type === COMP.CYL_SINGLE) {
            return [
                { id: 'A', x: 0.0, y: -0.27, z: -0.70, isChamber: true, isRear: true, isHydra: false }
            ];
        }
        if (type === COMP.CYL_DOUBLE) {
            return [
                { id: 'A', x: 0.0, y: -0.27, z: -0.70, isChamber: true, isRear: true, isHydra: false },
                { id: 'B', x: 0.0, y: -0.27, z:  0.70, isChamber: true, isFront: true, isHydra: false }
            ];
        }
        if (type === COMP.CYL_HYDRA) {
            return [
                { id: 'A', x: 0.0, y: -0.27, z: -0.70, isChamber: true, isRear: true, isHydra: true },
                { id: 'B', x: 0.0, y: -0.27, z:  0.70, isChamber: true, isFront: true, isHydra: true }
            ];
        }
        return [];
    }

    function getComponentFluidPorts(node) {
        if (!node) return [];
        const local = getLocalComponentFluidPorts(node.type);
        return local.map(p => {
            const rotP = rotateGridOffset(p, node.rot || 0);
            return {
                ...p,
                x: Number((node.x + rotP.x).toFixed(4)),
                y: Number(((node.y !== undefined ? node.y : 0) + (p.y !== undefined ? p.y : -0.32)).toFixed(4)),
                z: Number((node.z + rotP.z).toFixed(4)),
                lx: p.x,
                ly: p.y !== undefined ? p.y : -0.32,
                lz: p.z,
                nodeX: node.x,
                nodeY: node.y,
                nodeZ: node.z,
                label: p.id
            };
        });
    }

    function getComponentPins(node) {
        const rot = node.rot || 0;
        const pins = { inputs: [], outputs: [] };

        if (GATE_TYPES.has(node.type) || COMBINATIONAL_TYPES.has(node.type) || node.type === COMP.DISPLAY_7SEG || node.type === COMP.RELAY || CONTACTOR_TYPES.has(node.type) || CYLINDER_TYPES.has(node.type) || node.type === COMP.TIMER_TON || SUPPLY_TYPES.has(node.type) || VALVE_TYPES.has(node.type) || MACHINE_TYPES.has(node.type) || node.type === COMP.LAMP || node.type === COMP.LED_RED || node.type === COMP.BUZZER || node.type === COMP.SOLENOID_PNEU || node.type === COMP.SOLENOID_HYDRA) {
            const local = getLocalComponentPins(node.type, node.inputCount ?? 2, node.posS0, node.posS1);
            pins.outputs = local.outputs.map(pOut => {
                const outRot = rotateGridOffset(pOut, rot);
                const py = (pOut.y !== undefined) ? pOut.y : -0.38;
                return {
                    id: pOut.id,
                    aliases: pOut.aliases ? [...pOut.aliases] : [pOut.id],
                    x: Number((node.x + outRot.x).toFixed(4)),
                    y: Number(((node.y !== undefined ? node.y : 0) + py).toFixed(4)),
                    z: Number((node.z + outRot.z).toFixed(4)),
                    lx: pOut.x,
                    ly: py,
                    lz: pOut.z,
                    label: pOut.label || pOut.id
                };
            });
            pins.inputs = local.inputs.map(pIn => {
                const inRot = rotateGridOffset(pIn, rot);
                const py = (pIn.y !== undefined) ? pIn.y : -0.38;
                return {
                    id: pIn.id,
                    aliases: pIn.aliases ? [...pIn.aliases] : [pIn.id],
                    x: Number((node.x + inRot.x).toFixed(4)),
                    y: Number(((node.y !== undefined ? node.y : 0) + py).toFixed(4)),
                    z: Number((node.z + inRot.z).toFixed(4)),
                    lx: pIn.x,
                    ly: py,
                    lz: pIn.z,
                    label: pIn.label || pIn.id
                };
            });
        } else if (node.type === COMP.SWITCH || BUTTON_TYPES.has(node.type)) {
            const outLocal = { x: 0, y: -0.38, z: GRID_SIZE };
            const outRot = rotateGridOffset(outLocal, rot);
            const pId = node.switchId || node.btnId || 'OUT';
            const nodeBaseY = node.y !== undefined ? node.y : 0;
            const aliases = Array.from(new Set([pId, 'OUT', node.switchId, node.btnId].filter(Boolean)));
            pins.outputs.push({
                id: pId,
                aliases: aliases,
                x: snap(node.x + outRot.x),
                y: Number((nodeBaseY - 0.38).toFixed(4)),
                z: snap(node.z + outRot.z),
                lx: outLocal.x,
                ly: -0.38,
                lz: outLocal.z,
                label: pId
            });
        } else if (ACTUATOR_TYPES.has(node.type)) {
            const inLocal = { x: 0, y: -0.38, z: -GRID_SIZE };
            const inRot = rotateGridOffset(inLocal, rot);
            const nodeBaseY = node.y !== undefined ? node.y : 0;
            pins.inputs.push({
                id: 'A1',
                aliases: ['A1', 'X1', '1'],
                x: snap(node.x + inRot.x),
                y: Number((nodeBaseY - 0.38).toFixed(4)),
                z: snap(node.z + inRot.z),
                lx: inLocal.x,
                ly: -0.38,
                lz: inLocal.z,
                label: 'A1'
            });
        } else if (node.type === COMP.WIRE || node.type === 'JUMPER') {
            const py = -0.47;
            const p = { id: 'P1', x: snap(node.x), y: Number(((node.y !== undefined ? node.y : 0) + py).toFixed(4)), z: snap(node.z), lx: 0, ly: py, lz: 0, label: 'P1' };
            pins.inputs.push(p);
            pins.outputs.push(p);
        }
        return pins;
    }

    function traceUpstreamSwitches(startNode) {
        const foundSwitches = new Set();
        const visitedNodes = new Set();

        function dfs(currentNode) {
            if (!currentNode || visitedNodes.has(currentNode)) return;
            visitedNodes.add(currentNode);

            if (currentNode.type === COMP.SWITCH) {
                foundSwitches.add(currentNode.switchId);
                return;
            }

            if (CYLINDER_TYPES.has(currentNode.type) && currentNode.linkedValve) {
                dfs(currentNode.linkedValve);
                return;
            }

            if (VALVE_TYPES.has(currentNode.type)) {
                if (currentNode.linkedSolenoidY1) dfs(currentNode.linkedSolenoidY1);
                if (currentNode.linkedSolenoidY2) dfs(currentNode.linkedSolenoidY2);
            }

            const inPins = getComponentPins(currentNode).inputs;

            (state.airWires || []).forEach(aw => {
                const isTargetTo = (Math.abs(aw.to.nodeX - currentNode.x) < 0.25 && Math.abs(aw.to.nodeZ - currentNode.z) < 0.25);
                const isTargetFrom = (Math.abs(aw.from.nodeX - currentNode.x) < 0.25 && Math.abs(aw.from.nodeZ - currentNode.z) < 0.25);
                if (isTargetTo) {
                    const srcNode = state.circuit.get(key(aw.from.nodeX, aw.from.nodeY || 0, aw.from.nodeZ));
                    if (srcNode) dfs(srcNode);
                } else if (isTargetFrom) {
                    const srcNode = state.circuit.get(key(aw.to.nodeX, aw.to.nodeY || 0, aw.to.nodeZ));
                    if (srcNode) dfs(srcNode);
                }
            });

            const nodeBaseY = currentNode.y !== undefined ? currentNode.y : 0;
            inPins.forEach(pIn => {
                const directNode = state.circuit.get(key(pIn.x, nodeBaseY, pIn.z)) ||
                                   state.circuit.get(key(snap(pIn.x), 0, snap(pIn.z)));
                if (directNode) {
                    if (directNode.type === COMP.SWITCH) {
                        foundSwitches.add(directNode.switchId);
                    } else if (directNode.type === COMP.WIRE || directNode.type === 'JUMPER') {
                        traceWireBack(directNode.x, directNode.y || 0, directNode.z, new Set());
                    } else {
                        dfs(directNode);
                    }
                } else {
                    // Checa se há fio PCB vizinho ao pino de entrada
                    for (const other of state.circuit.values()) {
                        if ((other.type === COMP.WIRE || other.type === 'JUMPER') && Math.hypot(other.x - pIn.x, other.z - pIn.z) <= 0.35) {
                            traceWireBack(other.x, other.y || 0, other.z, new Set());
                        }
                    }
                }
            });
        }

        function traceWireBack(wx, wy, wz, seenWires) {
            const wKey = key(wx, wy, wz);
            if (seenWires.has(wKey)) return;
            seenWires.add(wKey);

            state.circuit.forEach(otherNode => {
                if (otherNode.type === COMP.SWITCH) {
                    const outP = getComponentPins(otherNode).outputs[0];
                    const distPin = outP ? Math.hypot(outP.x - wx, outP.z - wz) : Infinity;
                    const distBody = Math.hypot(otherNode.x - wx, otherNode.z - wz);
                    if (distPin <= 0.6 || distBody <= 0.6) {
                        foundSwitches.add(otherNode.switchId);
                    }
                } else if (GATE_TYPES.has(otherNode.type) || COMBINATIONAL_TYPES.has(otherNode.type) || otherNode.type === COMP.RELAY) {
                    const outs = getComponentPins(otherNode).outputs;
                    if (outs.some(o => Math.hypot(o.x - wx, o.z - wz) <= 0.5)) {
                        dfs(otherNode);
                    }
                }
            });

            (state.airWires || []).forEach(aw => {
                const matchTo = (Math.abs(aw.to.nodeX - wx) < 0.25 && Math.abs(aw.to.nodeZ - wz) < 0.25);
                const matchFrom = (Math.abs(aw.from.nodeX - wx) < 0.25 && Math.abs(aw.from.nodeZ - wz) < 0.25);
                if (matchTo) {
                    const srcNode = state.circuit.get(key(aw.from.nodeX, aw.from.nodeY || 0, aw.from.nodeZ));
                    if (srcNode) {
                        if (srcNode.type === COMP.WIRE || srcNode.type === 'JUMPER') traceWireBack(srcNode.x, srcNode.y || 0, srcNode.z, seenWires);
                        else dfs(srcNode);
                    }
                } else if (matchFrom) {
                    const srcNode = state.circuit.get(key(aw.to.nodeX, aw.to.nodeY || 0, aw.to.nodeZ));
                    if (srcNode) {
                        if (srcNode.type === COMP.WIRE || srcNode.type === 'JUMPER') traceWireBack(srcNode.x, srcNode.y || 0, srcNode.z, seenWires);
                        else dfs(srcNode);
                    }
                }
            });

            const neighbors = [[0, GRID_SIZE], [0, -GRID_SIZE], [GRID_SIZE, 0], [-GRID_SIZE, 0]];
            neighbors.forEach(([dx, dz]) => {
                const nb = state.circuit.get(key(wx + dx, wy, wz + dz));
                if (nb && (nb.type === COMP.WIRE || nb.type === 'JUMPER')) {
                    traceWireBack(nb.x, nb.y, nb.z, seenWires);
                }
            });
        }

        dfs(startNode);
        const list = Array.from(foundSwitches).sort();
        if (list.length === 0) {
            // Se nenhuma chave estiver conectada, usa as entradas da porta como variáveis para o mapa
            const inCount = startNode.inputCount || 2;
            return ['A', 'B', 'C', 'D'].slice(0, Math.max(2, inCount));
        }
        return list;
    }

    function simulateCircuitForInputVector(targetNode, inputValues) {
        const savedStates = new Map();
        state.circuit.forEach(n => {
            if (n.type === COMP.SWITCH) savedStates.set(n.switchId, n.manualValue);
        });

        Object.keys(inputValues).forEach(swId => {
            state.circuit.forEach(n => {
                if (n.type === COMP.SWITCH && n.switchId === swId) {
                    n.manualValue = Boolean(inputValues[swId]);
                }
            });
        });

        if (window.LogicCraft.simulation && window.LogicCraft.simulation.propagateSignals) {
            window.LogicCraft.simulation.propagateSignals();
        }

        // Se o nó for porta pura desconectada de chaves, avalia diretamente pelo vetor de entradas
        let res = Boolean(targetNode.state);
        if (GATE_TYPES.has(targetNode.type) && (!targetNode.evaluatedInputs || targetNode.evaluatedInputs.length === 0)) {
            const vals = Object.values(inputValues);
            res = computeGateLogic(targetNode.type, vals);
        }

        savedStates.forEach((val, swId) => {
            state.circuit.forEach(n => {
                if (n.type === COMP.SWITCH && n.switchId === swId) {
                    n.manualValue = val;
                }
            });
        });

        if (window.LogicCraft.simulation && window.LogicCraft.simulation.propagateSignals) {
            window.LogicCraft.simulation.propagateSignals();
        }

        return res;
    }

    function getKarnaughLayout(nVars, varNames = ['A', 'B', 'C', 'D']) {
        if (nVars === 2) {
            return {
                nVars: 2,
                rowVars: [varNames[0]],
                colVars: [varNames[1]],
                rows: ['0', '1'],
                cols: ['0', '1']
            };
        }
        if (nVars === 3) {
            return {
                nVars: 3,
                rowVars: [varNames[0]],
                colVars: [varNames[1], varNames[2]],
                rows: ['0', '1'],
                cols: ['00', '01', '11', '10']
            };
        }
        return {
            nVars: 4,
            rowVars: [varNames[0], varNames[1]],
            colVars: [varNames[2], varNames[3]],
            rows: ['00', '01', '11', '10'],
            cols: ['00', '01', '11', '10']
        };
    }

    function buildKarnaughMap(node) {
        let activeSwitches = traceUpstreamSwitches(node);
        if (activeSwitches.length < 2) {
            activeSwitches = activeSwitches.length === 1 ? [activeSwitches[0], 'B'] : ['A', 'B'];
        }
        if (activeSwitches.length > 4) activeSwitches = activeSwitches.slice(0, 4);

        const nVars = activeSwitches.length;
        const layout = getKarnaughLayout(nVars, activeSwitches);
        const totalCombinations = Math.pow(2, nVars);
        const table = [];

        for (let i = 0; i < totalCombinations; i++) {
            const inputs = {};
            activeSwitches.forEach((swName, idx) => {
                const shift = nVars - 1 - idx;
                inputs[swName] = ((i >> shift) & 1) === 1;
            });
            const out = simulateCircuitForInputVector(node, inputs);
            table.push({ minterm: i, inputs, output: out ? 1 : 0 });
        }

        const tableMap = new Map();
        table.forEach(r => tableMap.set(r.minterm, r));

        const grid = [];
        layout.rows.forEach((rLabel, rIdx) => {
            const rowCells = [];
            layout.cols.forEach((cLabel, cIdx) => {
                const minterm = Number.parseInt(rLabel + cLabel, 2);
                const rowData = tableMap.get(minterm);
                rowCells.push({
                    row: rIdx,
                    col: cIdx,
                    minterm: minterm,
                    value: rowData ? rowData.output : 0,
                    rowLabel: rLabel,
                    colLabel: cLabel
                });
            });
            grid.push(rowCells);
        });

        return { layout, grid, table, isBcd: false, activeSwitches };
    }

    function buildBcdKarnaughMap(segmentId) {
        const layout = getKarnaughLayout(4, ['A', 'B', 'C', 'D']);
        const mintermsVal1 = getBcdSegmentMinterms(segmentId);
        const dontCareList = getBcdDontCareMinterms();

        const grid = [];
        layout.rows.forEach((rLabel, rIdx) => {
            const rowCells = [];
            layout.cols.forEach((cLabel, cIdx) => {
                const m = Number.parseInt(rLabel + cLabel, 2);
                let displayVal = 0;
                if (mintermsVal1.includes(m)) displayVal = 1;
                else if (dontCareList.includes(m)) displayVal = 'X';
                rowCells.push({
                    row: rIdx, col: cIdx, minterm: m,
                    value: displayVal,
                    rowLabel: rLabel, colLabel: cLabel
                });
            });
            grid.push(rowCells);
        });
        return { layout, grid, mintermsVal1, dontCareList, isBcd: true, segmentId, activeSwitches: ['A', 'B', 'C', 'D'] };
    }

    function validateArbitraryKarnaughGroup(selectedMinterms, kData) {
        if (!selectedMinterms || selectedMinterms.length === 0) {
            return { valid: false, message: 'Selecione as células antes de salvar o agrupamento.' };
        }

        const size = selectedMinterms.length;
        if ((size & (size - 1)) !== 0 || size > 16) {
            return { 
                valid: false, 
                message: `Quantidade de células (${size}) inválida. O agrupamento deve conter 1, 2, 4, 8 ou 16 células.` 
            };
        }

        const cellMap = new Map();
        let containsOne = false;

        for (let r = 0; r < kData.grid.length; r++) {
            for (let c = 0; c < kData.grid[0].length; c++) {
                const cell = kData.grid[r][c];
                cellMap.set(cell.minterm, { r, c, val: cell.value, minterm: cell.minterm });
            }
        }

        for (const m of selectedMinterms) {
            const cInfo = cellMap.get(m);
            if (!cInfo) return { valid: false, message: `Mintermo m${m} inexistente neste mapa.` };
            if (cInfo.val === 0) {
                return { valid: false, message: `Agrupamento inválido: a célula m${m} tem nível lógico 0.` };
            }
            if (cInfo.val === 1) containsOne = true;
        }

        if (!containsOne) {
            return { valid: false, message: 'O agrupamento precisa conter ao menos uma célula 1 (não pode ser puramente X).' };
        }

        if (size === 1 || size === 16) {
            return { valid: true, minterms: [...selectedMinterms].sort((a,b) => a-b) };
        }

        const maxR = kData.grid.length;
        const maxC = kData.grid[0].length;
        const selectedSet = new Set(selectedMinterms);

        const candidateShapes = {
            2: [{ h: 1, w: 2 }, { h: 2, w: 1 }],
            4: [{ h: 1, w: 4 }, { h: 4, w: 1 }, { h: 2, w: 2 }],
            8: [{ h: 2, w: 4 }, { h: 4, w: 2 }]
        }[size] || [];

        for (const shape of candidateShapes) {
            if (shape.h > maxR || shape.w > maxC) continue;

            for (let r0 = 0; r0 < maxR; r0++) {
                for (let c0 = 0; c0 < maxC; c0++) {
                    const groupMinterms = [];
                    for (let dr = 0; dr < shape.h; dr++) {
                        for (let dc = 0; dc < shape.w; dc++) {
                            const r = (r0 + dr) % maxR;
                            const c = (c0 + dc) % maxC;
                            groupMinterms.push(kData.grid[r][c].minterm);
                        }
                    }

                    if (groupMinterms.length === selectedSet.size && groupMinterms.every(m => selectedSet.has(m))) {
                        return { valid: true, minterms: groupMinterms.sort((a,b) => a-b) };
                    }
                }
            }
        }

        return { 
            valid: false, 
            message: 'As células selecionadas não formam um agrupamento retangular adjacente válido.' 
        };
    }

    function extractKarnaughTerm(minterms, layout) {
        if (!minterms || minterms.length === 0) return '';
        const vars = layout.rowVars.concat(layout.colVars);
        const nBits = vars.length;

        if (minterms.length === Math.pow(2, nBits)) return '1';

        const literals = [];
        for (let i = 0; i < nBits; i++) {
            const bitValues = new Set(minterms.map(m => (m >> (nBits - 1 - i)) & 1));
            if (bitValues.size === 1) {
                const bit = [...bitValues][0];
                literals.push(bit === 1 ? vars[i] : `¬${vars[i]}`);
            }
        }

        return literals.length > 0 ? literals.join('') : '1';
    }

    function toggleKarnaughCellSelection(minterm) {
        if (!state.selectedKarnaughMinterms) state.selectedKarnaughMinterms = [];
        const idx = state.selectedKarnaughMinterms.indexOf(minterm);
        if (idx >= 0) {
            state.selectedKarnaughMinterms.splice(idx, 1);
        } else {
            state.selectedKarnaughMinterms.push(minterm);
        }
    }

    function saveCurrentKarnaughGroup(kData) {
        if (!state.selectedKarnaughMinterms || state.selectedKarnaughMinterms.length === 0) {
            return { success: false, message: 'Nenhuma célula selecionada.' };
        }

        const valResult = validateArbitraryKarnaughGroup(state.selectedKarnaughMinterms, kData);
        if (!valResult.valid) {
            return { success: false, message: valResult.message };
        }

        const minterms = valResult.minterms;
        const keyStr = minterms.join(',');

        if (state.karnaughGroups.some(g => g.minterms.join(',') === keyStr)) {
            return { success: false, message: 'Este agrupamento já foi salvo anteriormente.' };
        }

        const expr = extractKarnaughTerm(minterms, kData.layout);
        const groupData = {
            id: state.nextGroupId++,
            size: minterms.length,
            minterms: minterms,
            expression: expr
        };

        state.karnaughGroups.push(groupData);
        state.selectedKarnaughMinterms = [];

        return { 
            success: true, 
            message: `Agrupamento de ${groupData.size} células salvo: m(${minterms.join(', ')}). Termo: ${expr}`,
            group: groupData 
        };
    }

    function clearKarnaughGroups() {
        state.karnaughGroups = [];
        state.selectedKarnaughMinterms = [];
        return { success: true, message: 'Todos os agrupamentos foram limpos.' };
    }

    function serializeCircuit() {
        const list = [];
        state.circuit.forEach(node => {
            list.push({
                type: node.type,
                x: node.x, y: node.y, z: node.z,
                rot: node.rot,
                switchId: node.switchId,
                btnId: node.btnId || null,
                isPressed: Boolean(node.isPressed),
                isEmergencyLocked: Boolean(node.isEmergencyLocked),
                manualValue: Boolean(node.manualValue),
                inputCount: node.inputCount,
                tag: node.tag || null,
                openingPercent: typeof node.openingPercent === 'number' ? node.openingPercent : 100,
                delayTime: typeof node.delayTime === 'number' ? node.delayTime : 2.0,
                elapsedTime: typeof node.elapsedTime === 'number' ? node.elapsedTime : 0.0,
                extension: typeof node.extension === 'number' ? node.extension : 0.0,
                targetExtension: typeof node.targetExtension === 'number' ? node.targetExtension : 0.0,
                spoolPosition: typeof node.spoolPosition === 'number' ? node.spoolPosition : 0.0,
                motionState: node.motionState || 'RECUADO',
                sensorS0: Boolean(node.sensorS0),
                sensorS1: Boolean(node.sensorS1),
                posS0: typeof node.posS0 === 'number' ? node.posS0 : 0.05,
                posS1: typeof node.posS1 === 'number' ? node.posS1 : 0.95,
                pressureBar: typeof node.pressureBar === 'number' ? node.pressureBar : 0,
                state: Boolean(node.state),
                outputStates: node.outputStates ? { ...node.outputStates } : undefined
            });
        });
        return list;
    }

    function deserializeCircuit(jsonArray) {
        if (!Array.isArray(jsonArray)) {
            throw new Error('Formato inválido: esperado um array de componentes.');
        }
        const validTypes = new Set(Object.values(COMP));
        return jsonArray.map((item, idx) => {
            if (!item || typeof item.type !== 'string' || !validTypes.has(item.type)) {
                throw new Error(`Componente inválido na posição ${idx}: tipo desconhecido.`);
            }
            if (typeof item.x !== 'number' || typeof item.y !== 'number' || typeof item.z !== 'number') {
                throw new Error(`Componente inválido na posição ${idx}: coordenadas ausentes.`);
            }
            return {
                type: item.type,
                x: item.x, y: item.y, z: item.z,
                rot: Number.isInteger(item.rot) ? ((item.rot % 4) + 4) % 4 : 0,
                switchId: ['A', 'B', 'C', 'D'].includes(item.switchId) ? item.switchId : 'A',
                btnId: item.btnId ? String(item.btnId) : null,
                isPressed: Boolean(item.isPressed),
                isEmergencyLocked: Boolean(item.isEmergencyLocked),
                manualValue: Boolean(item.manualValue),
                inputCount: Number.isInteger(item.inputCount) ? item.inputCount : undefined,
                tag: item.tag ? String(item.tag) : null,
                openingPercent: typeof item.openingPercent === 'number' ? item.openingPercent : 100,
                delayTime: typeof item.delayTime === 'number' ? item.delayTime : 2.0,
                elapsedTime: typeof item.elapsedTime === 'number' ? item.elapsedTime : 0.0,
                extension: typeof item.extension === 'number' ? item.extension : 0.0,
                targetExtension: typeof item.targetExtension === 'number' ? item.targetExtension : 0.0,
                spoolPosition: typeof item.spoolPosition === 'number' ? item.spoolPosition : 0.0,
                motionState: item.motionState || 'RECUADO',
                sensorS0: item.sensorS0 !== undefined ? Boolean(item.sensorS0) : true,
                sensorS1: item.sensorS1 !== undefined ? Boolean(item.sensorS1) : false,
                posS0: typeof item.posS0 === 'number' ? item.posS0 : 0.05,
                posS1: typeof item.posS1 === 'number' ? item.posS1 : 0.95,
                pressureBar: typeof item.pressureBar === 'number' ? item.pressureBar : 0,
                state: Boolean(item.state),
                outputStates: item.outputStates ? { ...item.outputStates } : undefined
            };
        });
    }

    return {
        GRID_SIZE, ACTUATOR_LINK_DISTANCE, HYDRAULIC_LINK_DISTANCE,
        COMP, GATE_TYPES, BUTTON_TYPES, COMBINATIONAL_TYPES, ACTUATOR_TYPES, VALVE_TYPES, CYLINDER_TYPES, SUPPLY_TYPES, FLOW_CONTROL_TYPES, MACHINE_TYPES, CONTACTOR_TYPES,
        INPUT_CONFIG, BCD_TABLE,
        snap, key, rotateGridOffset, getLocalGatePins, getLocalComponentPins, getComponentPins,
        getLocalComponentFluidPorts, getComponentFluidPorts,
        getDigitalWordState, computeGateLogic, generateTruthTable, getActiveTruthTableRow,
        getGateExpression, getDeMorganEquivalent, getKarnaughLayout, buildKarnaughMap, buildBcdKarnaughMap,
        validateArbitraryKarnaughGroup, extractKarnaughTerm, toggleKarnaughCellSelection,
        saveCurrentKarnaughGroup, clearKarnaughGroups, computeBcd7Segment,
        serializeCircuit, deserializeCircuit, pushHistory,
        saveCircuitToLocalStorage, loadCircuitFromLocalStorage
    };
})();

/**
 * LogicCraft - Módulo de Imersão Sensorial e Efeitos Sonoros Industriais (Web Audio API Nativa)
 * Síntese física em tempo real de contatores, escapes pneumáticos, sensores e motores
 */
window.LogicCraft.audio = (function() {
    let ctx = null;
    let isMuted = false;
    let masterGain = null;
    let motorOsc = null;
    let motorGain = null;
    let isInitialized = false;

    try {
        isMuted = localStorage.getItem('logiccraft_sound_muted') === 'true';
    } catch (_) {}

    function initAudio() {
        if (isInitialized && ctx) {
            if (ctx.state === 'suspended') ctx.resume().catch(() => {});
            return;
        }
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextClass) return;
            ctx = new AudioContextClass();
            masterGain = ctx.createGain();
            masterGain.gain.value = isMuted ? 0.0 : 0.40;
            masterGain.connect(ctx.destination);

            setupMotorSynth();
            isInitialized = true;
        } catch (e) {
            console.warn('[LogicCraft Audio] Falha ao inicializar Web Audio:', e);
        }
    }

    function setupMotorSynth() {
        if (!ctx || !masterGain) return;
        try {
            // Sintetizador acústico de motor trifásico (Zumbido 60 Hz + magnetostrição 120 Hz)
            motorOsc = ctx.createOscillator();
            motorOsc.type = 'sine';
            motorOsc.frequency.setValueAtTime(60, ctx.currentTime);

            const motorOscHarmonic = ctx.createOscillator();
            motorOscHarmonic.type = 'triangle';
            motorOscHarmonic.frequency.setValueAtTime(120, ctx.currentTime);

            const filter = ctx.createBiquadFilter();
            filter.type = 'lowpass';
            filter.frequency.setValueAtTime(130, ctx.currentTime);

            motorGain = ctx.createGain();
            motorGain.gain.setValueAtTime(0, ctx.currentTime);

            motorOsc.connect(filter);
            motorOscHarmonic.connect(filter);
            filter.connect(motorGain);
            motorGain.connect(masterGain);

            motorOsc.start();
            motorOscHarmonic.start();
        } catch (_) {}
    }

    const unlockEvents = ['click', 'keydown', 'touchstart', 'pointerdown'];
    function unlock() {
        initAudio();
        if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
            unlockEvents.forEach(evt => window.removeEventListener(evt, unlock));
        }
    }
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        unlockEvents.forEach(evt => window.addEventListener(evt, unlock, { once: true, passive: true }));
    }

    // Gerenciador de polifonia e debounce contra sobreposição e distorção
    const lastSoundTimes = {};
    function canPlay(category, minIntervalSec = 0.045) {
        if (!ctx) return false;
        const now = ctx.currentTime;
        if (lastSoundTimes[category] && (now - lastSoundTimes[category] < minIntervalSec)) {
            return false;
        }
        lastSoundTimes[category] = now;
        return true;
    }

    function toggleMute() {
        isMuted = !isMuted;
        try {
            localStorage.setItem('logiccraft_sound_muted', isMuted ? 'true' : 'false');
        } catch (_) {}
        if (masterGain && ctx) {
            masterGain.gain.setTargetAtTime(isMuted ? 0 : 0.40, ctx.currentTime, 0.03);
        }
        if (isMuted) stopAllAudio();
        return isMuted;
    }

    function isSoundMuted() {
        return isMuted;
    }

    function playClick(isRelease = false) {
        if (isMuted || !ctx) return;
        if (!canPlay('click', 0.035)) return;
        try {
            const now = ctx.currentTime;
            // Transiente tátil de microswitch / botoeira industrial (Snap mecânico)
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'triangle';
            const startFreq = isRelease ? 580 : 780;
            const endFreq = isRelease ? 340 : 480;
            osc.frequency.setValueAtTime(startFreq, now);
            osc.frequency.exponentialRampToValueAtTime(endFreq, now + 0.012);

            gain.gain.setValueAtTime(0.18, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.015);

            osc.connect(gain);
            gain.connect(masterGain);
            osc.start(now);
            osc.stop(now + 0.016);
            osc.onended = () => { try { osc.disconnect(); gain.disconnect(); } catch (_) {} };

            // Micro-impacto acústico (click físico seco)
            const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.006), ctx.sampleRate);
            const d = buf.getChannelData(0);
            for (let i = 0; i < d.length; i++) {
                d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (d.length * 0.25));
            }
            const noise = ctx.createBufferSource();
            noise.buffer = buf;
            const f = ctx.createBiquadFilter();
            f.type = 'highpass';
            f.frequency.setValueAtTime(2400, now);
            const nGain = ctx.createGain();
            nGain.gain.setValueAtTime(0.12, now);
            nGain.gain.exponentialRampToValueAtTime(0.001, now + 0.006);
            noise.connect(f);
            f.connect(nGain);
            nGain.connect(masterGain);
            noise.start(now);
            noise.onended = () => { try { noise.disconnect(); f.disconnect(); nGain.disconnect(); } catch (_) {} };
        } catch (_) {}
    }

    function playContactorClack(isOpening = false) {
        if (isMuted || !ctx) return;
        if (!canPlay('contactor', 0.050)) return;
        try {
            const now = ctx.currentTime;
            // 1. Golpe magnético do núcleo do contator (armadura móvel atracando)
            const osc = ctx.createOscillator();
            const oscGain = ctx.createGain();
            osc.type = 'sine';
            const baseFreq = isOpening ? 180 : 95;
            const endFreq = isOpening ? 65 : 45;
            osc.frequency.setValueAtTime(baseFreq, now);
            osc.frequency.exponentialRampToValueAtTime(endFreq, now + 0.045);

            oscGain.gain.setValueAtTime(isOpening ? 0.22 : 0.35, now);
            oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.048);

            osc.connect(oscGain);
            oscGain.connect(masterGain);
            osc.start(now);
            osc.stop(now + 0.05);
            osc.onended = () => { try { osc.disconnect(); oscGain.disconnect(); } catch (_) {} };

            // 2. Ruído de impacto mecânico com amortecimento realista
            const bufSize = Math.floor(ctx.sampleRate * (isOpening ? 0.020 : 0.030));
            const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
            const data = buf.getChannelData(0);
            for (let i = 0; i < bufSize; i++) {
                data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufSize * 0.28));
            }
            const noise = ctx.createBufferSource();
            noise.buffer = buf;
            const filter = ctx.createBiquadFilter();
            filter.type = 'bandpass';
            filter.frequency.setValueAtTime(isOpening ? 1800 : 920, now);
            filter.Q.setValueAtTime(1.4, now);

            const nGain = ctx.createGain();
            nGain.gain.setValueAtTime(isOpening ? 0.16 : 0.28, now);
            nGain.gain.exponentialRampToValueAtTime(0.001, now + (isOpening ? 0.020 : 0.030));

            noise.connect(filter);
            filter.connect(nGain);
            nGain.connect(masterGain);
            noise.start(now);
            noise.onended = () => { try { noise.disconnect(); filter.disconnect(); nGain.disconnect(); } catch (_) {} };
        } catch (_) {}
    }

    function playPneumaticPuff(duration = 0.18, intensity = 0.26) {
        if (isMuted || !ctx) return;
        if (!canPlay('puff', 0.065)) return;
        try {
            const now = ctx.currentTime;
            // Escape de ar filtrado por silenciador de bronze sinterizado ("psshhh" suave com duração limitada)
            const safeDuration = Math.min(duration, 0.30);
            const bufSize = Math.floor(ctx.sampleRate * safeDuration);
            const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
            const data = buf.getChannelData(0);
            for (let i = 0; i < bufSize; i++) {
                data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufSize * 0.36));
            }
            const noise = ctx.createBufferSource();
            noise.buffer = buf;

            const filter = ctx.createBiquadFilter();
            filter.type = 'bandpass';
            filter.frequency.setValueAtTime(1350, now);
            filter.Q.setValueAtTime(1.1, now);

            const gain = ctx.createGain();
            gain.gain.setValueAtTime(intensity, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + safeDuration);

            noise.connect(filter);
            filter.connect(gain);
            gain.connect(masterGain);
            noise.start(now);
            noise.stop(now + safeDuration);
            noise.onended = () => { try { noise.disconnect(); filter.disconnect(); gain.disconnect(); } catch (_) {} };
        } catch (_) {}
    }

    function playSensorBeep() {
        if (isMuted || !ctx) return;
        if (!canPlay('sensor', 0.055)) return;
        try {
            const now = ctx.currentTime;
            // Bip sutil de instrumento de bancada (curto, suave e com limite de 28ms)
            [1175, 1760].forEach((freq, idx) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, now);

                gain.gain.setValueAtTime(0.05 / (idx + 1), now);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.026);

                osc.connect(gain);
                gain.connect(masterGain);
                osc.start(now);
                osc.stop(now + 0.028);
                osc.onended = () => { try { osc.disconnect(); gain.disconnect(); } catch (_) {} };
            });
        } catch (_) {}
    }

    function playMetalClink() {
        if (isMuted || !ctx) return;
        if (!canPlay('metal', 0.070)) return;
        try {
            const now = ctx.currentTime;
            [1850, 2920, 4200].forEach((freq, idx) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, now);

                const dur = 0.08 - idx * 0.015;
                gain.gain.setValueAtTime(0.14 / (idx + 1), now);
                gain.gain.exponentialRampToValueAtTime(0.001, now + dur);

                osc.connect(gain);
                gain.connect(masterGain);
                osc.start(now);
                osc.stop(now + dur);
                osc.onended = () => { try { osc.disconnect(); gain.disconnect(); } catch (_) {} };
            });
        } catch (_) {}
    }

    function playPlasticThud() {
        if (isMuted || !ctx) return;
        if (!canPlay('plastic', 0.070)) return;
        try {
            const now = ctx.currentTime;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'triangle';
            osc.frequency.setValueAtTime(210, now);
            osc.frequency.exponentialRampToValueAtTime(65, now + 0.040);

            gain.gain.setValueAtTime(0.20, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.042);

            osc.connect(gain);
            gain.connect(masterGain);
            osc.start(now);
            osc.stop(now + 0.045);
            osc.onended = () => { try { osc.disconnect(); gain.disconnect(); } catch (_) {} };
        } catch (_) {}
    }

    let motorIdleTimeout = null;
    function setMotorRunning(isRunning) {
        if (!motorGain || !ctx) return;
        try {
            if (motorIdleTimeout) {
                clearTimeout(motorIdleTimeout);
                motorIdleTimeout = null;
            }
            if (isRunning && !isMuted) {
                // Ativa com ganho suave e define limite de segurança contra som infinito
                motorGain.gain.setTargetAtTime(0.11, ctx.currentTime, 0.06);
                motorIdleTimeout = setTimeout(() => {
                    if (motorGain && ctx) {
                        motorGain.gain.setTargetAtTime(0.0, ctx.currentTime, 0.25);
                    }
                }, 15000); // Se o usuário ficar inativo por 15s, silencia suavemente
            } else {
                motorGain.gain.setTargetAtTime(0.0, ctx.currentTime, 0.04);
            }
        } catch (_) {}
    }

    function playThermalTrip() {
        if (isMuted || !ctx) return;
        try {
            const now = ctx.currentTime;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'triangle';
            osc.frequency.setValueAtTime(240, now);
            osc.frequency.exponentialRampToValueAtTime(80, now + 0.05);

            gain.gain.setValueAtTime(0.28, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);

            osc.connect(gain);
            gain.connect(masterGain);
            osc.start(now);
            osc.stop(now + 0.055);
            osc.onended = () => { try { osc.disconnect(); gain.disconnect(); } catch (_) {} };
        } catch (_) {}
    }

    let buzzerOsc = null;
    let buzzerGain = null;
    let buzzerTimeout = null;
    function setBuzzerActive(active) {
        if (!ctx || !masterGain) return;
        try {
            if (buzzerTimeout) {
                clearTimeout(buzzerTimeout);
                buzzerTimeout = null;
            }
            if (active && !buzzerOsc && !isMuted) {
                buzzerOsc = ctx.createOscillator();
                buzzerGain = ctx.createGain();
                buzzerOsc.type = 'square';
                buzzerOsc.frequency.setValueAtTime(2200, ctx.currentTime);
                buzzerGain.gain.setValueAtTime(0.09, ctx.currentTime);
                buzzerOsc.connect(buzzerGain);
                buzzerGain.connect(masterGain);
                buzzerOsc.start();

                // Limite estrito de segurança: máximo de 3.0 segundos para não incomodar o aluno
                buzzerTimeout = setTimeout(() => {
                    setBuzzerActive(false);
                }, 3000);
            } else if (!active && buzzerOsc) {
                buzzerOsc.stop();
                buzzerOsc.disconnect();
                buzzerOsc = null;
                buzzerGain = null;
            }
        } catch (_) {}
    }

    function stopAllAudio() {
        try {
            if (buzzerTimeout) { clearTimeout(buzzerTimeout); buzzerTimeout = null; }
            if (motorIdleTimeout) { clearTimeout(motorIdleTimeout); motorIdleTimeout = null; }
            if (buzzerOsc) {
                buzzerOsc.stop();
                buzzerOsc.disconnect();
                buzzerOsc = null;
                buzzerGain = null;
            }
            if (motorGain && ctx) {
                motorGain.gain.setTargetAtTime(0.0, ctx.currentTime, 0.02);
            }
        } catch (_) {}
    }

    function playSuccessChime() {
        if (isMuted || !ctx || !masterGain) return;
        try {
            const now = ctx.currentTime;
            [523.25, 659.25, 783.99].forEach((freq, idx) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                const startTime = now + idx * 0.08;
                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, startTime);
                gain.gain.setValueAtTime(0.12, startTime);
                gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.25);
                osc.connect(gain);
                gain.connect(masterGain);
                osc.start(startTime);
                osc.stop(startTime + 0.26);
                osc.onended = () => { try { osc.disconnect(); gain.disconnect(); } catch (_) {} };
            });
        } catch (_) {}
    }

    return {
        initAudio,
        toggleMute,
        isSoundMuted,
        playClick,
        playContactorClack,
        playPneumaticPuff,
        playSensorBeep,
        playMetalClink,
        playPlasticThud,
        setMotorRunning,
        playThermalTrip,
        setBuzzerActive,
        stopAllAudio,
        playSuccessChime
    };
})();